import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    CircularProgress,
    Alert,
    Chip,
    Pagination,
    Stack,
    Tooltip,
} from '@mui/material';
import { ArrowLeft, Eye, Clock, Mail, CreditCard } from 'lucide-react';
import { getBookingsByRetreatId } from '../../Services/Bookings/BookingsApis';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import GlobalPagination from '../../Components/Common/Globalpagination';

const RetreatBookings = () => {
    const { retreatId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const retreatTitle = location.state?.retreatTitle || 'Retreat';

    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [pagination, setPagination] = useState({
        totalItems: 0,
        totalPages: 0,
        currentPage: 1,
        pageSize: 25
    });

    const fetchBookings = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getBookingsByRetreatId(retreatId, page, pageSize);
            if (response && response.success) {
                setBookings(response.data || []);
                const total = response.totalItems || response.totalRecords || response.total || (response.totalPages ? response.totalPages * pageSize : (response.data || []).length);
                const totalPagesCount = response.totalPages || Math.ceil(total / pageSize) || 1;
                setPagination({
                    totalItems: total,
                    totalPages: totalPagesCount,
                    currentPage: response.currentPage || page,
                    pageSize: response.pageSize || pageSize
                });
            }
        } catch (err) {
            setError('Failed to fetch bookings for this retreat');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [retreatId, page, pageSize]);

    useEffect(() => {
        fetchBookings();
    }, [fetchBookings]);

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const formatBookingStatus = (status) => {
        if (!status) return 'N/A';
        const map = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'cancelled': 'Cancelled',
            'rejected': 'Rejected',
            'completed': 'Completed',
            'awaiting_host_confirmation': 'Awaiting Host Confirmation',
            'date_change_requested': 'Date Change Requested',
            'date_change_rejected': 'Date Change Rejected',
            'date_change_approved': 'Date Change Approved',
            'user_cancelled_date_change_request': 'User Cancelled Date Change Request',
            'awaiting_admin_confirmation': 'Awaiting Admin Confirmation'
        };
        return map[status.toLowerCase()] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getStatusColor = (status, type) => {
        if (type === 'payment') {
            switch (status?.toLowerCase()) {
                case 'completed': return { bgcolor: '#E8F5E8', color: '#2E7D32' };
                case 'pending': return { bgcolor: '#FFF3E0', color: '#F57C00' };
                case 'failed': return { bgcolor: '#FFEBEE', color: '#C62828' };
                default: return { bgcolor: '#F5F5F5', color: '#666' };
            }
        } else {
            switch (status?.toLowerCase()) {
                case 'confirmed': return { bgcolor: '#E3F2FD', color: '#1976D2' };
                case 'completed': return { bgcolor: '#E8F5E8', color: '#2E7D32' };
                case 'pending': return { bgcolor: '#FFF8E1', color: '#F57C00' };
                case 'awaiting_host_confirmation': return { bgcolor: '#FFF4E5', color: '#ED6C02' };
                case 'awaiting_admin_confirmation': return { bgcolor: '#F3E5F5', color: '#9C27B0' };
                case 'date_change_requested': return { bgcolor: '#E1F5FE', color: '#0288D1' };
                case 'date_change_approved': return { bgcolor: '#E0F2F1', color: '#00897B' };
                case 'date_change_rejected': return { bgcolor: '#FCE4EC', color: '#D81B60' };
                case 'user_cancelled_date_change_request': return { bgcolor: '#ECEFF1', color: '#546E7A' };
                case 'cancelled': return { bgcolor: '#FFEBEE', color: '#C62828' };
                case 'rejected': return { bgcolor: '#FFEBEE', color: '#B71C1C' };
                default: return { bgcolor: '#F5F5F5', color: '#666' };
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header Section */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ bgcolor: '#FFF', border: '1px solid #E8E8E8', borderRadius: '12px' }}
                >
                    <ArrowLeft size={20} />
                </IconButton>
                <Box>
                    <Typography sx={{ color: '#222', fontSize: '1.75rem', fontWeight: 500 }}>
                        Retreat Bookings
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                        {retreatTitle}
                    </Typography>
                </Box>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <Paper sx={{ borderRadius: "12px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                <TableContainer>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>S No</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Booking ID</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>User Email</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Amount</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Payment Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Booking Status</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap' }}>Timeline</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.8px', py: 2, whiteSpace: 'nowrap', pr: 4 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                        <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                    </TableCell>
                                </TableRow>
                            ) : bookings.length > 0 ? (
                                bookings.map((booking, index) => (
                                    <TableRow key={booking.id || index}>
                                        <TableCell sx={{ fontWeight: 500 }}>{(page - 1) * 10 + index + 1}</TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>{booking.bookingId || booking.id}</TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Mail size={14} color="#666" />
                                                <Typography variant="body2">{booking.userEmail}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <CreditCard size={14} color="#34A853" />
                                                <Typography sx={{ fontWeight: 600, color: '#34A853' }}>
                                                    {booking.amountPaid}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={booking.paymentStatus}
                                                size="small"
                                                sx={{ ...getStatusColor(booking.paymentStatus, 'payment'), fontWeight: 600, fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={formatBookingStatus(booking.bookingStatus)}
                                                size="small"
                                                sx={{ ...getStatusColor(booking.bookingStatus, 'booking'), fontWeight: 600, fontSize: '0.75rem' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Clock size={14} color="#666" />
                                                <Typography variant="caption" sx={{ color: '#666' }}>
                                                    {formatDate(booking.createdAt)}
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => navigate('/bookings/view-details', { state: { booking: booking.id } })}
                                                    sx={{ color: '#FFBD65' }}
                                                >
                                                    <Eye size={18} />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                                        <Typography color="textSecondary">No bookings found for this retreat.</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <GlobalPagination
                    page={page}
                    pageSize={pageSize}
                    totalRecords={pagination.totalItems}
                    totalPages={pagination.totalPages}
                    onPageChange={(newPage) => {
                        setPage(newPage);
                    }}
                    onPageSizeChange={(newSize) => {
                        setPageSize(newSize);
                        setPage(1);
                    }}
                    loading={loading}
                />
            </Paper>
        </Box>
    );
};

export default RetreatBookings;
